import { useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { FileDown, Fingerprint, Upload, X } from "lucide-react";

// Identitas unit (INV-SN, brief owner 22 Sep 2026): laporan "Identitas perlu
// dirapikan" + impor Serial Number. Disisipkan SATU baris di tab "Unit & Riwayat
// IMEI" InventoryStok.jsx (pola PenyesuaianUnitPanel) supaya berkas halaman itu
// tidak bertabrakan saat digabung.
//
// IDENTIFIER TIDAK PERNAH DIUBAH dari sini — aksi hanya mengisi SN kedua,
// menandai "identifier ini SN", atau menandai sudah diperiksa.
//
// Gerbang tombol = kunci SAMA dengan backend (`routers/inventory/identitas_rute.py`):
// - lihat laporan      : inventory_unit.view
// - isi SN / impor / diperiksa : inventory_unit.update
// - tandai "ini SN"    : inventory_unit.update + role approver (require_approver)
const APPROVER_ROLES = ["finance_spv", "finance_ceo", "admin", "superadmin"];

const LABEL_KELAS = {
  gabungan_imei_sn: "IMEI + SN digabung",
  gabungan_dua_imei: "Dua IMEI (dual-SIM)",
  gabungan_lain: "Berisi pemisah",
  berhuruf_tunggal: "Berhuruf (kemungkinan SN)",
  digit_panjang: "Angka terlalu panjang",
  normal: "Normal",
};
const LABEL_BENDERA = {
  sn_awalan_s: "SN berawalan S (barcode Apple?)",
  sn_bentrok_identifier: "SN sama dengan IMEI/SN unit lain",
};
const LABEL_STATUS_IMPOR = {
  akan_diisi: "Akan diisi", diisi: "Diisi", sudah_sama: "Sudah sama", konflik: "Konflik — tidak ditimpa",
  imei_tak_dikenal: "IMEI tidak dikenal", perlu_review: "Perlu diperiksa", sn_dipakai_unit_lain: "SN dipakai unit lain",
  identifier_sn: "Identifier sudah SN", ganda_di_berkas: "Ganda di berkas", sel_tidak_sah: "Sel tidak sah",
};

export default function IdentitasUnitPanel({ onSelesai }) {
  const { can, user } = useAuth();
  const bolehLihat = can("inventory_unit", "view");
  const bolehUbah = can("inventory_unit", "update");
  const bolehApprover = bolehUbah && APPROVER_ROLES.includes(user?.role);
  const [laporan, setLaporan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [isianSN, setIsianSN] = useState({});
  const [impor, setImpor] = useState(null);   // { file, hasil }

  if (!bolehLihat) return null;

  const muat = async () => {
    setBusy(true);
    try {
      const r = await api.get("/inventory/units/identitas-perlu-dirapikan");
      setLaporan(r.data);
      const awal = {};
      (r.data.items || []).forEach(u => { awal[u.id] = u.saran?.sn?.[0]?.nilai || ""; });
      setIsianSN(awal);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat laporan identitas"); }
    finally { setBusy(false); }
  };

  const aksi = async (u, jenis) => {
    try {
      if (jenis === "isi") {
        const sn = (isianSN[u.id] || "").trim();
        if (!sn) { toast.error("Isi Serial Number dulu — periksa terhadap fisik HP"); return; }
        await api.post(`/inventory/units/${u.id}/serial-number`, { serial_number: sn });
        toast.success(`Serial Number ${u.identifier} diisi`);
      } else if (jenis === "tandai_sn") {
        const alasan = window.prompt(`Nyatakan "${u.identifier}" sebagai Serial Number (bukan IMEI)?\n\nAlasan (wajib):`, "");
        if (alasan === null) return;
        await api.post(`/inventory/units/${u.id}/identitas/tandai-sn`, { nilai: true, alasan });
        toast.success("Ditandai: identifier ini Serial Number");
      } else if (jenis === "diperiksa") {
        const catatan = window.prompt("Catatan pemeriksaan (opsional):", "");
        if (catatan === null) return;
        await api.post(`/inventory/units/${u.id}/identitas/diperiksa`, { catatan });
        toast.success("Ditandai sudah diperiksa");
      }
      await muat();
      onSelesai && onSelesai();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  const unduhTemplate = async () => {
    try {
      const r = await api.get("/inventory/units/serial-number/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Template_Impor_Serial_Number.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh template"); }
  };

  const kirimImpor = async (file, terapkan) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/inventory/units/serial-number/impor", fd, {
        headers: { "Content-Type": "multipart/form-data" }, params: { terapkan: !!terapkan },
      });
      setImpor({ file, hasil: r.data });
      if (terapkan) {
        toast.success(`${r.data.ringkasan?.diisi || 0} Serial Number diisi`);
        onSelesai && onSelesai();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membaca berkas"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="inv-identitas-panel">
      <button onClick={muat} disabled={busy} data-testid="inv-identitas-buka"
              className="h-9 px-3 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-1.5 hover:bg-slate-50">
        <Fingerprint className="w-4 h-4" /> Identitas perlu dirapikan
      </button>
      {bolehUbah && (
        <label className="h-9 px-3 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-1.5 hover:bg-slate-50 cursor-pointer">
          <Upload className="w-4 h-4" /> Impor Serial Number
          <input type="file" accept=".xlsx" className="hidden" data-testid="inv-impor-sn-file" disabled={busy}
                 onChange={e => { kirimImpor(e.target.files?.[0], false); e.target.value = ""; }} />
        </label>
      )}
      {bolehUbah && (
        <button onClick={unduhTemplate} data-testid="inv-impor-sn-template"
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-1.5 hover:bg-slate-50">
          <FileDown className="w-4 h-4" /> Template SN
        </button>
      )}

      {impor && (
        <div className="w-full bg-white border border-slate-200 rounded-lg p-3 space-y-2 text-xs" data-testid="inv-impor-sn-hasil">
          <div className="flex items-center justify-between">
            <p className="font-medium text-slate-700">
              {impor.hasil.diterapkan ? "Hasil impor" : "Pratinjau impor — belum ada yang disimpan"} ·{" "}
              {Object.entries(impor.hasil.ringkasan || {}).map(([k, n]) => `${LABEL_STATUS_IMPOR[k] || k}: ${n}`).join(" · ")}
            </p>
            <button onClick={() => setImpor(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-[11px] text-slate-500">
            Impor HANYA mengisi Serial Number yang masih kosong. SN yang sudah tercatat tidak pernah ditimpa.
          </p>
          <div className="max-h-56 overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead className="text-slate-500"><tr>
                <th className="text-left pr-2">Baris</th><th className="text-left pr-2">IMEI</th>
                <th className="text-left pr-2">Serial Number</th><th className="text-left">Status</th></tr></thead>
              <tbody>
                {(impor.hasil.baris || []).map(b => (
                  <tr key={b.baris} className="border-t border-slate-100">
                    <td className="pr-2 font-mono">{b.baris}</td>
                    <td className="pr-2 font-mono">{b.imei || "—"}</td>
                    <td className="pr-2 font-mono">{b.sn || "—"}</td>
                    <td className="text-slate-600">{LABEL_STATUS_IMPOR[b.status] || b.status}{b.catatan ? ` — ${b.catatan}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!impor.hasil.diterapkan && (impor.hasil.ringkasan?.akan_diisi || 0) > 0 && (
            <button onClick={() => kirimImpor(impor.file, true)} disabled={busy} data-testid="inv-impor-sn-terapkan"
                    className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium">
              Terapkan — isi {impor.hasil.ringkasan.akan_diisi} Serial Number
            </button>
          )}
        </div>
      )}

      {laporan && (
        <div className="w-full bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid="inv-identitas-laporan">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-800">
              Identitas perlu dirapikan · {laporan.count} unit
            </p>
            <button onClick={() => setLaporan(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-[11px] text-slate-500">
            IMEI/SN utama tidak pernah diubah otomatis — periksa fisik HP lalu isi Serial Number di kolomnya
            sendiri. "Kembar fisik" = HP yang sama kemungkinan tercatat dua kali.
          </p>
          {/* Daftar terpotong tidak boleh terbaca "bersih" — sebutkan terang-terangan. */}
          {laporan.terpotong && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
               data-testid="inv-identitas-terpotong">
              Daftar ini TERPOTONG — masih ada unit lain yang perlu dirapikan. Rapikan/tandai diperiksa
              yang tampil dulu, lalu muat ulang.
            </p>
          )}
          {laporan.items.length === 0 && !laporan.terpotong && (
            <p className="text-xs text-slate-400">Tidak ada unit yang perlu dirapikan.</p>
          )}
          {laporan.items.map(u => (
            <div key={u.id} className="border-t border-slate-100 pt-2 text-xs space-y-1" data-testid={`inv-identitas-${u.id}`}>
              <p className="font-mono text-slate-900">{u.identifier}
                <span className="ml-2 font-sans text-[11px] text-amber-700">{LABEL_KELAS[u.kelas] || u.kelas}</span>
                {(u.bendera || []).map(b => (
                  <span key={b} className="ml-2 font-sans text-[11px] text-rose-700">{LABEL_BENDERA[b] || b}</span>
                ))}
              </p>
              <p className="text-slate-500">
                {u.nama} · {u.warehouse_nama || "—"} · {u.status}
                {u.serial_number && <> · SN <span className="font-mono">{u.serial_number}</span></>}
                {u.jumlah_nota > 0 && <> · disebut di {u.jumlah_nota} nota</>}
                {(u.klaim_cashback || []).length > 0 && <> · {u.klaim_cashback.length} klaim cashback</>}
              </p>
              {(u.saran?.imei || []).length > 0 && (
                <p className="text-slate-500">Saran IMEI: {u.saran.imei.map(s => `${s.nilai}${s.luhn_sah === false ? " (Luhn tidak sah)" : ""}`).join(", ")}</p>
              )}
              {(u.kembar_fisik || []).length > 0 && (
                <p className="text-rose-700">Kembar fisik: {u.kembar_fisik.map(k => `${k.identifier} (${k.status})`).join(", ")}</p>
              )}
              {bolehUbah && (
                <div className="flex flex-wrap items-center gap-2">
                  {!u.serial_number && !u.identifier_adalah_sn && (
                    <>
                      <input value={isianSN[u.id] || ""} placeholder="Serial Number"
                             onChange={e => setIsianSN(s => ({ ...s, [u.id]: e.target.value }))}
                             data-testid={`inv-identitas-sn-${u.id}`}
                             className="h-8 px-2 border border-slate-200 rounded text-xs font-mono w-44" />
                      <button onClick={() => aksi(u, "isi")} data-testid={`inv-identitas-isi-${u.id}`}
                              className="h-8 px-2.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]">Isi SN</button>
                    </>
                  )}
                  {bolehApprover && u.kelas === "berhuruf_tunggal" && !u.identifier_adalah_sn && !u.serial_number && (
                    <button onClick={() => aksi(u, "tandai_sn")} data-testid={`inv-identitas-tandai-sn-${u.id}`}
                            className="h-8 px-2.5 rounded border border-slate-300 text-[11px] hover:bg-slate-50">Identifier ini SN</button>
                  )}
                  <button onClick={() => aksi(u, "diperiksa")} data-testid={`inv-identitas-diperiksa-${u.id}`}
                          className="h-8 px-2.5 rounded border border-slate-300 text-[11px] hover:bg-slate-50">Sudah diperiksa</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
